import { Injectable, signal } from '@angular/core';

import type {
  AuthoritativeTileResult,
  LiveTileState,
  ValidatedLiveTileSnapshot,
} from './live-tile.model';

const INITIAL_STATE: LiveTileState = {
  clientCode: null,
  day: null,
  values: null,
  source: null,
  producedAt: null,
  authoritativeCompletedAt: null,
  lastLivePublishedAt: null,
  lastLivePublishedAtUtc: null,
};

export type LiveTileApplyOutcome =
  | 'applied'
  | 'client-mismatch'
  | 'day-mismatch'
  | 'no-authoritative-baseline'
  | 'stale-or-duplicate';

@Injectable()
export class LiveTileStore {
  private readonly stateValue = signal<LiveTileState>(INITIAL_STATE);
  private liveAcceptanceOpen = false;

  readonly state = this.stateValue.asReadonly();

  applyAuthoritative(result: AuthoritativeTileResult): void {
    this.liveAcceptanceOpen = true;
    const current = this.stateValue();
    // The ordering floor is PRESERVED across refreshes (same client + day): a
    // delayed pre-refresh snapshot's publishedAtUtc cannot exceed the last
    // applied one, so it is rejected even after the baseline is replaced. The
    // display clock starts at the browser-side completion instant.
    const sameScope = current.clientCode === result.clientCode && current.day === result.day;
    this.stateValue.set({
      clientCode: result.clientCode,
      day: result.day,
      values: result.values,
      source: 'authoritative',
      producedAt: result.completedAt,
      authoritativeCompletedAt: result.completedAt,
      lastLivePublishedAt: sameScope ? current.lastLivePublishedAt : null,
      lastLivePublishedAtUtc: sameScope ? current.lastLivePublishedAtUtc : null,
    });
  }

  requireAuthoritativeRefresh(): void {
    this.liveAcceptanceOpen = false;
  }

  /**
   * Tenant transition (FR-005): drop the previous tenant's values entirely and
   * close live acceptance until the new tenant's authoritative baseline lands.
   * Called by the realtime subscriber the moment the session client changes or
   * the session loses eligibility (`null`) — the previous tenant's data is
   * never displayed under a different or absent identity.
   */
  beginTenantSession(clientCode: string | null): void {
    this.liveAcceptanceOpen = false;
    this.stateValue.set({
      clientCode,
      day: null,
      values: null,
      source: null,
      producedAt: null,
      authoritativeCompletedAt: null,
      lastLivePublishedAt: null,
      lastLivePublishedAtUtc: null,
    });
  }

  beginUtcDay(day: LiveTileState['day'] & string): void {
    const current = this.stateValue();
    this.liveAcceptanceOpen = false;
    this.stateValue.set({
      clientCode: current.clientCode,
      day,
      values: null,
      source: null,
      producedAt: null,
      authoritativeCompletedAt: null,
      lastLivePublishedAt: null,
      lastLivePublishedAtUtc: null,
    });
  }

  applyLive(snapshot: ValidatedLiveTileSnapshot): LiveTileApplyOutcome {
    const current = this.stateValue();
    const publishedAt = Date.parse(snapshot.publishedAtUtc);
    if (current.clientCode !== null && current.clientCode !== snapshot.clientCode) {
      return 'client-mismatch';
    }
    if (current.day !== null && current.day !== snapshot.day) return 'day-mismatch';
    if (!this.liveAcceptanceOpen || current.values === null) {
      return 'no-authoritative-baseline';
    }
    // Ordering is the publisher's `publishedAtUtc` (strictly increasing per
    // group, frozen v1 contract) — `sequence` is diagnostic-only and can
    // decrease across a hub rescale, so it never participates. The floor is
    // compared at full fractional precision: raw RFC 3339 is not
    // chronologically sortable when the fractional part is optional (`...00.1Z`
    // is newer than `...00Z` but compares lexicographically smaller; `...00.1Z`
    // and `...00.10Z` denote the same instant). Preserved across authoritative
    // refreshes. Only the publisher's clock is compared.
    if (
      current.lastLivePublishedAtUtc !== null &&
      compareUtcInstants(snapshot.publishedAtUtc, current.lastLivePublishedAtUtc) <= 0
    ) {
      return 'stale-or-duplicate';
    }

    this.stateValue.set({
      clientCode: snapshot.clientCode,
      day: snapshot.day,
      values: {
        eventCount: snapshot.eventCount,
        totalTokens: snapshot.totalTokens,
        spendAmount: snapshot.spendAmount,
        spendCurrency: snapshot.spendCurrency,
        errorCount: snapshot.errorCount,
      },
      source: 'live',
      producedAt: publishedAt,
      authoritativeCompletedAt: current.authoritativeCompletedAt,
      lastLivePublishedAt: publishedAt,
      lastLivePublishedAtUtc: snapshot.publishedAtUtc,
    });
    return 'applied';
  }
}

/**
 * Compares validated RFC 3339 UTC instants without converting fractional
 * seconds to a precision-limited numeric representation.
 */
function compareUtcInstants(left: string, right: string): number {
  const leftSecond = left.slice(0, 19);
  const rightSecond = right.slice(0, 19);
  if (leftSecond !== rightSecond) return leftSecond < rightSecond ? -1 : 1;

  const leftFraction = left.length > 20 ? left.slice(20, -1) : '';
  const rightFraction = right.length > 20 ? right.slice(20, -1) : '';
  const fractionWidth = Math.max(leftFraction.length, rightFraction.length);
  const normalizedLeft = leftFraction.padEnd(fractionWidth, '0');
  const normalizedRight = rightFraction.padEnd(fractionWidth, '0');
  if (normalizedLeft === normalizedRight) return 0;
  return normalizedLeft < normalizedRight ? -1 : 1;
}
